import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import ScheduleHeader from '../../components/Schedule/ScheduleHeader/ScheduleHeader';
import ScheduleTable from '../../components/Schedule/ScheduleTable/ScheduleTable';
import AltScheduleTable from '../../components/Schedule/AltScheduleTable/AltScheduleTable';
import "./Schedule.scss";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import PostService from '../../API/PostService';
import { useFetching } from '../../hooks/useFetching';
import ModalComponent from '../../components/UI/ModalComponent/ModalComponent';
import SpecialistForm from '../../components/Specialists/SpecialistForm/SpecialistForm/SpecialistForm';
import ClinicsForm from '../../components/Clinics/ClinicsForm/ClinicsForm/ClinicsForm';
import SlotForm from '../../components/Slot/SlotForm/SlotForm';
import { setPositionsAction, setSpecialistsLeavesAction } from '../../store/specialistsReducer';
import {
    setSpecialistsAction,
    setSalonsAction,
    setUnderfilledSlotsAction,
    setIsAltScheduleAction
} from '../../store/scheduleReducer';
import AltSlotForm from '../../components/Slot/AltSlotForm/AltSlotForm';

const Schedule = () => {
    const { specialistId, salonId, slotId, altSlotId } = useParams();
    const scheduleHeaderRef = useRef(null);
    const lastScrollTop = useRef(0);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [totalWorekerTime, setTotalWorekerTime] = useState({});
    const [pending, setPending] = useState(true);
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);

    // Получаем данные из редьюсера
    const {
        specialists,
        salons,
        slots,
        currentSchedule,
        underfilledSlots,
        isAltSchedule
    } = useSelector(state => state.schedule);

    const currentUser = useSelector(state => state.currentUser.currentUser);
    const specialistsLeaves = useSelector(state => state.specialists.specialistsLeaves);

    const [fetchClinic] = useFetching(async (id) => {
        const response = await PostService.getClinicById(id);
        if (response.data.success && response.data.result) {
            return response.data.result;
        }
        return null;
    });

    const [fetchPositions] = useFetching(async () => {
        const response = await PostService.getPositions();
        if (response.data.success && response.data.result) {
            dispatch(setPositionsAction(response.data.result));
        }
    });

    // Получаем общее время работы специалистов
    const [fetchTotalWorkersTime] = useFetching(async (scheduleId) => {
        const response = await PostService.getTotalWorkerTime(scheduleId);
        
        if (response.data.success && response.data.result) {
            setTotalWorekerTime(response.data.result);
            setPending(false);
        } else {
            setTotalWorekerTime({});
            setPending(false);
        }
    });

    const [fetchUnderfilledSlots] = useFetching(async () => {
        const response = await PostService.getUnderfilledSpots(currentSchedule);

        if (response.data.success && response.data.result) {
            dispatch(setUnderfilledSlotsAction(response.data.result));
        }
    });

    const [fetchSpecialists] = useFetching(async () => {
        const response = await PostService.getWorkers();
        if (response.data.success && response.data.result) {
            // Устанавливаем общее время работы специалистов
            const specialistArray = response.data.result.map((specialist, index) => ({
                ...specialist,
                total_hours: totalWorekerTime?.[specialist.id] || 0,
                orderIndex: index,
            }));
            dispatch(setSpecialistsAction(specialistArray));
        }
    });

    const [fetchSalons] = useFetching(async () => {
        const response = await PostService.getWorkerSites();
        if (response.data.success && response.data.result) {
            dispatch(setSalonsAction(response.data.result));
        }
    });

    const [fetchListWorkerAbsenceData] = useFetching(async () => {
        const response = await PostService.getListWorkerAbsenceData(currentSchedule);
        if (response.data.success && response.data.result) {
            dispatch(setSpecialistsLeavesAction(response.data.result));
        }
    });

    // Ref для таблицы расписания
    const tableRef = useRef(null);

    // Обработчик скролла таблицы
    const handleTableScroll = useCallback(() => {
        if (!tableRef.current) return;

        const scrollTop = tableRef.current.scrollTop;
        
        if (scrollTop > lastScrollTop.current && scrollTop > 10) {
            // Скролл вниз и не в самом верху
            setIsHeaderHidden(true);
        } else if (scrollTop < lastScrollTop.current && scrollTop <= 0) {
            // Скролл вверх и близко к верху таблицы
            setIsHeaderHidden(false);
        }
        
        lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    }, []);

    // Функция для установки ref таблицы
    const setTableRef = useCallback((ref) => {
        if (ref) {
            tableRef.current = ref;
            // Добавляем обработчик скролла к таблице
            ref.addEventListener('scroll', handleTableScroll, { passive: true });
        }
    }, [handleTableScroll]);

    // Убираем обработчик при размонтировании
    useEffect(() => {
        return () => {
            if (tableRef.current) {
                tableRef.current.removeEventListener('scroll', handleTableScroll);
            }
        };
    }, [handleTableScroll]);

    useEffect(() => {
        fetchPositions();
        fetchSalons();

        if (salonId) fetchClinic(salonId);

        // Проверяем, нужно ли отображать альтернативный график по гет параметру, только при первой загрузке страницы
        if (isAltSchedule == null) dispatch(setIsAltScheduleAction(searchParams.get('view') === 'alt'));
    }, []);

    useEffect(() => {
        if (!pending) fetchSpecialists();
    }, [totalWorekerTime]);

    useEffect(() => {
        fetchUnderfilledSlots();
        fetchListWorkerAbsenceData();
        fetchTotalWorkersTime(currentSchedule);
    }, [slots, currentSchedule]);

    const closeModalHandler = useCallback(() => {
        navigate('/schedule/');
    }, [navigate]);

    // При нажатии на кнопку в ScheduleHeader переключаем альтернативный график
    const toggleAltSchedule = useCallback(() => {
        dispatch(setIsAltScheduleAction(!isAltSchedule));
    }, [isAltSchedule, dispatch]);

    // Ставим гет параметр view=alt, когда нужно отображать альтернативный график
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        if (isAltSchedule && !newParams.has('view')) {
            newParams.set('view', 'alt');
        } else if (!isAltSchedule) {
            newParams.delete('view');
        }

        setSearchParams(newParams);
    }, [isAltSchedule, setSearchParams, location.pathname]);

    const currentSpecialist = useMemo(() => {
        return specialistId ? specialists.find(s => s.id == specialistId) : null;
    }, [specialistId, specialists]);

    const currentSalon = useMemo(() => {
        return salonId ? salons.find(s => s.id == salonId) : null;
    }, [salonId, salons])

    const currentSlot = useMemo(() => {
        return slotId ? slots.find(s => s.id == slotId) : null;
    }, [slotId, slots]);

    const currentAltSlot = useMemo(() => {
        return altSlotId ? slots.find(s => s.id == altSlotId) : null;
    }, [altSlotId, slots]);

    return (
        <div className="schedule">
            <ScheduleHeader 
                ref={scheduleHeaderRef}
                toggleAltSchedule={toggleAltSchedule} 
                isAltSchedule={isAltSchedule} 
                isHeaderHidden={isHeaderHidden}
            />

            {isAltSchedule ?
                <AltScheduleTable 
                    ref={setTableRef}
                    specialists={specialists} 
                    salons={salons} 
                    slots={slots} 
                    currentSchedule={currentSchedule} 
                    specialistsLeaves={specialistsLeaves} 
                />
                :
                <ScheduleTable 
                    ref={setTableRef}
                    specialists={specialists} 
                    salons={salons} 
                    slots={slots} 
                    currentSchedule={currentSchedule} 
                    underfilledSlots={underfilledSlots} 
                />
            }
            {!currentUser?.employee_id &&
                <>
                    {currentSpecialist && (
                        <ModalComponent isOpen closeHandler={closeModalHandler} isFormModal>
                            <SpecialistForm id={specialistId} specialist={currentSpecialist} />
                        </ModalComponent>
                    )}

                    {currentSalon && (
                        <ModalComponent isOpen closeHandler={closeModalHandler} isFormModal>
                            <ClinicsForm id={salonId} salon={currentSalon} />
                        </ModalComponent>
                    )}

                    {currentSlot && (
                        <ModalComponent isOpen isSlotsForm closeHandler={closeModalHandler}>
                            <SlotForm closeModal={closeModalHandler} id={slotId} slot={currentSlot} />
                        </ModalComponent>
                    )}

                    {currentAltSlot && (
                        <ModalComponent isOpen isSlotsForm closeHandler={closeModalHandler}>
                            <AltSlotForm closeModal={closeModalHandler} id={altSlotId} slot={currentAltSlot} listId={currentSchedule} />
                        </ModalComponent>
                    )}
                </>
            }
        </div>
    );
};

export default Schedule;